import React, { useState, useEffect, useCallback } from 'react'
import { Box, Stack, Loader, Group, Paper } from '@mantine/core'
import { AppBadge, AppButton, AppInput, AppText, LegalConsentCheckbox } from '../app/common/ui'
import { FiSearch, FiX, FiExternalLink } from 'react-icons/fi'
import { observer } from 'mobx-react-lite'
import agent from '../app/api/agent'
import { useStore } from '../app/stores/store'
import { extractThumbnailFromVideo } from './ThumbnailExtractor'
import type { FirearmModel } from '../app/models/firearmModel'
import { useLegalText } from '../app/hooks/useLegalText'
import { toast } from 'react-toastify'

const ShootsDetailsScreen = observer(function ShootsDetailsScreen () {
  const { editorStore, uploadDrawerStore } = useStore()
  const [firearmModels, setFirearmModels] = useState<FirearmModel[]>([])
  const [cartridgeOptions, setCartridgeOptions] = useState<{ value: string; label: string }[]>([])
  const [listsLoading, setListsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [consentGiven, setConsentGiven] = useState(false)
  const { text: consentText } = useLegalText('slop-feed-consent')

  const selectedModel = firearmModels.find(m => m.id === editorStore.linkedFirearmModelId)
  const filteredModels = firearmModels.filter(m =>
    `${m.make} ${m.model} ${m.caliber}`.toLowerCase().includes(searchTerm.toLowerCase())
  ).slice(0, 8)

  useEffect(() => {
    let cancelled = false
    setListsLoading(true)
    void (async () => {
      try {
        const [fm, cs] = await Promise.all([
          agent.FirearmModels.list({ pageSize: 100 }),
          agent.CartridgeSpecs.list({ pageSize: 80 })
        ])
        if (cancelled) return
        setFirearmModels(fm.items)
        setCartridgeOptions(
          cs.items.map((c) => ({
            value: c.id,
            label: (c.shortName || c.name || '').trim() || c.id
          }))
        )
      } catch {
        if (!cancelled) {
          setFirearmModels([])
          setCartridgeOptions([])
        }
      } finally {
        if (!cancelled) setListsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const handlePost = useCallback(async () => {
    const photoUrl = editorStore.photoUrl
    const mediaType = editorStore.mediaType
    if (!photoUrl || !mediaType) return

    if (!consentGiven) {
      toast.error('You must agree to the Slop Feed consent terms.')
      return
    }

    let thumbnailBlob: Blob | undefined
    if (mediaType === 'video') {
      try {
        thumbnailBlob = await extractThumbnailFromVideo(photoUrl)
      } catch {
        /* optional thumbnail */
      }
    }
    await editorStore.postShoot(
      () => void uploadDrawerStore.afterSuccessfulShootFromEditor(),
      thumbnailBlob,
      { accepted: true, version: consentText?.version ?? 1 }
    )
  }, [consentGiven, consentText?.version, editorStore, uploadDrawerStore])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: '100%',
        backgroundColor: 'var(--gs-bg-2)',
        borderRadius: 8,
        overflow: 'hidden',
        border: '1px solid var(--gs-border)'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 12px',
          borderBottom: '1px solid var(--gs-border)',
          flexShrink: 0
        }}
      >
        <AppButton variant="subtle" size="sm" compact onClick={() => uploadDrawerStore.backToEditor()} sx={{ fontWeight: 600 }}>
          &lt; Back
        </AppButton>
        <AppButton
          variant="filled"
          color="orange"
          size="sm"
          radius="xl"
          loading={editorStore.isPosting}
          disabled={editorStore.isPosting || !editorStore.canPost}
          onClick={() => void handlePost()}
          px="lg"
        >
          Post
        </AppButton>
      </Box>

      <Box sx={{ padding: 12, flex: 1, minHeight: 0, overflow: 'auto' }}>
        {listsLoading
          ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 'xl' }}>
            <Loader size="sm" />
          </Box>
            )
          : (
          <Stack spacing="md">
            {/* Firearm Model Section */}
            <Box>
              <AppText size="sm" fw={600} mb={8}>Link a firearm model (optional)</AppText>
              <AppText size="xs" c="dimmed" mb={12}>
                Connect this video to a firearm from the registry to help others find related content
              </AppText>

              <AppInput
                placeholder="Search firearm models..."
                icon={<FiSearch size={16} />}
                size="sm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.currentTarget.value)}
                mb={selectedModel ? 12 : 8}
              />

              {/* Selected Model Display */}
              {selectedModel && (
                <Paper p="sm" mb={12} sx={{ backgroundColor: 'var(--gs-surface2)', border: '1px solid var(--gs-border)', borderRadius: 6 }}>
                  <Group position="apart">
                    <Box>
                      <AppText size="sm" fw={600}>{selectedModel.make} {selectedModel.model}</AppText>
                      <Group spacing={6} mt={4}>
                        {selectedModel.caliber && (
                          <AppBadge size="xs" variant="light">{selectedModel.caliber}</AppBadge>
                        )}
                        {selectedModel.platform && (
                          <AppBadge size="xs" variant="light">{selectedModel.platform}</AppBadge>
                        )}
                      </Group>
                    </Box>
                    <AppButton
                      variant="subtle"
                      size="xs"
                      color="gray"
                      onClick={() => editorStore.setLinkedFirearmModelId(null)}
                    >
                      <FiX size={16} />
                    </AppButton>
                  </Group>
                </Paper>
              )}

              {/* Model Search Results */}
              {searchTerm && (
                <Stack spacing={6} mb={8}>
                  {filteredModels.length > 0
                    ? filteredModels.map(model => (
                      <Paper
                        key={model.id}
                        p={8}
                        sx={{
                          backgroundColor: model.id === editorStore.linkedFirearmModelId ? 'var(--gs-surface3)' : 'var(--gs-surface2)',
                          border: `1px solid ${model.id === editorStore.linkedFirearmModelId ? '#E85D04' : 'var(--gs-border)'}`,
                          borderRadius: 6,
                          cursor: 'pointer',
                          transition: 'all 0.15s',
                          '&:hover': {
                            borderColor: '#E85D04',
                            backgroundColor: 'var(--gs-surface3)'
                          }
                        }}
                        onClick={() => editorStore.setLinkedFirearmModelId(model.id)}
                      >
                        <AppText size="sm" fw={600}>{model.make} {model.model}</AppText>
                        <Group spacing={6} mt={4}>
                          {model.caliber && <AppBadge size="xs" variant="light">{model.caliber}</AppBadge>}
                          {model.platform && <AppBadge size="xs" variant="light">{model.platform}</AppBadge>}
                        </Group>
                      </Paper>
                    ))
                    : (
                      <AppText size="xs" c="dimmed">No models found. Browse the registry to discover more.</AppText>
                    )}
                </Stack>
              )}

              <Group spacing={8} mt={8}>
                <AppButton
                  variant="default"
                  size="xs"
                  rightIcon={<FiExternalLink size={14} />}
                  component="a"
                  href="/registry"
                  target="_blank"
                >
                  Browse Full Registry
                </AppButton>
              </Group>
            </Box>

            {/* Cartridge Section */}
            <Box>
              <AppText size="sm" fw={600} mb={8}>Cartridge/Ammunition (optional)</AppText>
              <AppText size="xs" c="dimmed" mb={8}>
                Specify what you were shooting to help viewers understand the context
              </AppText>
              {/* Cartridge select */}
              <Box
                component="select"
                value={editorStore.linkedCartridgeSpecId || ''}
                onChange={(e) => editorStore.setLinkedCartridgeSpecId(e.currentTarget.value || null)}
                sx={{
                  width: '100%',
                  padding: '8px 12px',
                  fontSize: 14,
                  borderRadius: 4,
                  border: '1px solid var(--gs-border)',
                  backgroundColor: 'var(--gs-surface2)',
                  color: 'var(--gs-text)',
                  cursor: 'pointer',
                  '&:hover': { borderColor: '#E85D04' },
                  '&:focus': { outline: 'none', borderColor: '#E85D04' }
                }}
              >
                <option value="">None</option>
                {cartridgeOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Box>
            </Box>

            <LegalConsentCheckbox
              legalTextKey="slop-feed-consent"
              checked={consentGiven}
              onChange={setConsentGiven}
            />
          </Stack>
            )}

        {editorStore.error && (
          <AppText size="sm" c="red" mt="xs">
            {editorStore.error}
          </AppText>
        )}
      </Box>
    </Box>
  )
})

export default ShootsDetailsScreen
